import React, { useCallback, useEffect, useRef, useState } from 'react';
import Layout from '../components/Layout';
import { authService } from '../services/authService';
import signalSourcesService from '../services/signalSourcesService';
import ProposalQueue from '../components/signalsources/ProposalQueue';
import CompanyGroup from '../components/signalsources/CompanyGroup';
import EditBar from '../components/signalsources/EditBar';
import AddSourceDialog from '../components/signalsources/AddSourceDialog';
import StartWatchingDialog from '../components/signalsources/StartWatchingDialog';
import EvidenceDialog from '../components/signalsources/EvidenceDialog';
import { NoCompanies, NoneInThisView, LoadingCompanies } from '../components/signalsources/EmptyStates';

// The Signal Sources page: the companies a tenant monitors and the pages watched for
// each, led by a ranked queue of decisions awaiting them.
//
// One page, no tabs (parent spec SS9). Market sources are never shown — they have no
// tenant state and no tenant UI (SS2).
//
// Design: docs/superpowers/specs/2026-07-29-signal-sources-v5-page-design.md

const FILTERS = [
  { id: 'monitoring', label: 'Monitoring' },
  { id: 'unwatched', label: 'Not watched' },
  { id: 'all', label: 'All' },
];

export default function SignalSourcesPage() {
  const [user, setUser] = useState(null);
  const [page, setPage] = useState(null);
  // ONE queue. The API still ranks the two populations separately (they are
  // different SQL shapes), but the page interleaves them into a single list so the
  // ranking a user sees is whole: the strongest company wins the top slot whether
  // or not we happen to hold a page for it.
  const [proposals, setProposals] = useState([]);
  const [queueCounts, setQueueCounts] = useState({ watch: 0, needs_page: 0 });
  const [pageError, setPageError] = useState('');
  const [queueError, setQueueError] = useState('');
  const [show, setShow] = useState(null); // null until the first payload decides (V5-6)
  const [mode, setMode] = useState('view');
  const [open, setOpen] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [busyKeys, setBusyKeys] = useState(new Set());
  // Two separate channels, not one: `notice` is a success confirmation (the dark
  // pill); `actionError` is a failed action (styled like the page's other errors —
  // pageError/queueError/dialogError — instead of looking like a confirmation).
  const [notice, setNotice] = useState('');
  const [actionError, setActionError] = useState('');
  // Guards the whole-queue accept so a second click cannot fire the loop again.
  const [watchAllBusy, setWatchAllBusy] = useState(false);
  // The reason a user clicked to inspect, and the records behind it.
  const [evidence, setEvidence] = useState(null);
  // The company awaiting a switch-on confirmation, and what we know about it.
  const [watchConfirm, setWatchConfirm] = useState(null);
  // Companies the user has just toggled, kept on screen even once the refetch stops
  // matching the current filter. Switching a company off used to make its row vanish
  // instantly — the one thing the user just acted on, gone with no confirmation of
  // what happened. Cleared when the filter changes, because choosing a filter is
  // asking to see what matches it and holding a row past that makes the filter lie.
  const [justToggled, setJustToggled] = useState([]);
  // Companies loaded beyond the first page. Held separately from `page` so a
  // refetch of page one — which happens after every decision — cannot silently
  // discard what the user has already scrolled past.
  const [more, setMore] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  // True only while a NEW list is being fetched — the first load and a filter
  // change. Deliberately NOT set by refetchBoth, which runs after every accept,
  // dismiss and toggle: blanking the list there would flash on every click and take
  // the just-toggled row off screen, which is the confirmation the user is looking
  // for. A background refresh should be invisible; a new question should not be.
  const [listLoading, setListLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  // Set when the dialog is opened straight off accepting a company that has no page
  // on record, so it lands on that company instead of the first in the list.
  const [dialogPrefill, setDialogPrefill] = useState(null);
  const [dialogError, setDialogError] = useState('');
  // Independent of the current filter/page: `companies` above is the FILTERED,
  // paginated slice (e.g. empty on 'Not watched', capped at 50 on 'Monitoring'),
  // which would leave Add permanently disabled or incomplete. The dialog needs the
  // full monitored set, fetched fresh each time it opens.

  // In-flight guards for the two multi-step actions (EditBar's bulk actions and the
  // Add-source dialog), so a double-click cannot re-fire the whole loop. State
  // drives the `busy` prop (disables the buttons); the ref is the actual guard —
  // state updates inside an event handler don't apply until the handler's
  // synchronous portion finishes, so two clicks dispatched back to back would both
  // still see the OLD state value in their closures. The ref mutates immediately
  // and is shared by every closure, so it catches that case too.
  const bulkBusyRef = useRef(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const addBusyRef = useRef(false);
  const [addBusy, setAddBusy] = useState(false);

  // Transient notices clear themselves — a toast that stays forever eventually
  // looks like a stuck/permanent state, and an old success message lingering
  // through a later, unrelated action is misleading.
  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(''), 5000);
    return () => clearTimeout(timer);
  }, [notice]);
  useEffect(() => {
    if (!actionError) return undefined;
    const timer = setTimeout(() => setActionError(''), 5000);
    return () => clearTimeout(timer);
  }, [actionError]);

  // The user's filter choice wins for the rest of the session: a filter that
  // reasserts itself while you work is worse than a poor default (V5-6).
  const filterTouched = useRef(false);

  // Guards against request ordering: a slow request (e.g. the cold start's own
  // read, or a filter click that happens to be slow) racing a faster, later one
  // must not win just because its response happens to arrive last. Every call
  // stamps the sequence it was issued at; a response only gets to setPage/
  // setPageError if it is still the most recently ISSUED call when it resolves.
  const pageSeq = useRef(0);

  // fresh=true means the user asked a NEW question (first load, or a new filter),
  // so the list is blanked to a skeleton rather than left showing the old answer.
  const loadPage = useCallback(async (which, fresh = false) => {
    // Stamped at issuance, not resolution — clearing the error here always
    // corresponds to the most-recently-ISSUED call, so it is safe unconditionally.
    const seq = ++pageSeq.current;
    try {
      setPageError('');
      if (fresh) setListLoading(true);
      const data = await signalSourcesService.getPage({ show: which, limit: PAGE_SIZE });
      if (seq !== pageSeq.current) return null; // superseded by a newer request
      setPage(data);
      return data;
    } catch (e) {
      if (seq !== pageSeq.current) return null; // superseded by a newer request
      setPageError(e.message);
      return null;
    } finally {
      // Guarded on the sequence for the same reason the payload is: a superseded
      // request must not clear the skeleton belonging to the newer one.
      if (seq === pageSeq.current) setListLoading(false);
    }
  }, []);

  // The queue is a PAGE of the backlog, not the whole of it — a real tenant can have
  // hundreds waiting. The size is named so "monitor all" can say honestly that more
  // may remain rather than implying it cleared everything.
  const QUEUE_PAGE = 25;

  // One screen of companies. The list is 13,570 rows on the dev tenant, so this is
  // a window onto it and Show more is how the rest is reached.
  const PAGE_SIZE = 50;

  const loadQueue = useCallback(async () => {
    try {
      setQueueError('');
      const data = await signalSourcesService.getProposals({ limit: QUEUE_PAGE });
      // Already ONE ranked list. Ranking stays server-side, where scoreOf lives and
      // where the signal it needs (role volume, contacts, recency) actually is —
      // none of that reaches the wire, and SS6 says the score never should.
      setProposals(data.proposals || []);
      setQueueCounts(data.counts || { watch: 0, needs_page: 0 });
    } catch (e) {
      setQueueError(e.message);
    }
  }, []);

  // First load. `show` is unknown until the counts arrive, so the initial read asks
  // for 'monitoring' and re-reads as 'unwatched' if nothing is monitored — the cold
  // start leads with the queue rather than a blank list.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      authService.getCurrentUser().then((u) => !cancelled && setUser(u)).catch(() => {});
      const first = await loadPage('monitoring', true);
      if (cancelled) return;
      // Gated so a cold start that lands AFTER the user has already chosen a filter
      // (a slow first response racing a quick click, or any future re-run of this
      // effect) cannot silently override that choice.
      if (!filterTouched.current) {
        if (first && first.counts.monitored === 0 && first.counts.total > 0) {
          setShow('unwatched');
          await loadPage('unwatched', true);
        } else {
          setShow('monitoring');
        }
      }
      await loadQueue();
    })();
    return () => { cancelled = true; };
  }, [loadPage, loadQueue]);

  const refetchBoth = useCallback(async () => {
    await Promise.all([loadPage(show || 'monitoring'), loadQueue()]);
  }, [loadPage, loadQueue, show]);

  const chooseFilter = (id) => {
    filterTouched.current = true;
    setJustToggled([]); // the filter is the question; kept rows would contradict it
    setMore([]);        // and so would the previous filter's accumulated pages
    setShow(id);
    loadPage(id, true);
  };

  // busyKeysRef mirrors the busyKeys state as the actual re-entrancy guard, for the
  // same reason bulkBusyRef/addBusyRef do (see above): two clicks on the same
  // switch dispatched back to back can both still read the OLD busyKeys state from
  // their closures, before React has applied the first one's update.
  const busyKeysRef = useRef(new Set());

  const withBusy = async (key, fn) => {
    if (busyKeysRef.current.has(key)) return;
    busyKeysRef.current.add(key);
    setBusyKeys((s) => new Set(s).add(key));
    try {
      await fn();
    } catch (e) {
      // A failed toggle must not read as a success confirmation — a separate
      // channel from `notice`, styled like the page's other errors.
      setActionError(e.message);
    } finally {
      busyKeysRef.current.delete(key);
      setBusyKeys((s) => { const n = new Set(s); n.delete(key); return n; });
    }
  };

  const decide = (proposal, action) => {
    // Accepting a needs-page proposal is not a decide call at all: there is nothing
    // to accept until the user supplies a URL. Asking for it IS the action, and the
    // dialog writes the source and switches monitoring on in one request.
    if (action === 'accept' && proposal.kind === 'needs_page') {
      setDialogError('');
      setDialogPrefill({ companyId: proposal.company_id, companyName: proposal.company });
      setDialog(true);
      return Promise.resolve();
    }
    return withBusy(proposal.dedupe_key, async () => {
      await signalSourcesService.decide({
        kind: proposal.kind,
        dedupeKey: proposal.dedupe_key,
        action,
        // Re-posted from the RENDERED row, not rebuilt, so the write acts on exactly
        // what the user was shown.
        payload: { company_id: proposal.company_id },
        pages: proposal.pages || [],
      });
      setNotice(acceptNotice(proposal, action));
      await refetchBoth();
    });
  };

  // supplyPage is the inline capture's whole job: one request that writes the
  // source, records the URL on the company, and starts the watching — the API does
  // all three in one transaction, so there is no half-applied state to recover from.
  const supplyPage = (proposal, url, sourceType) =>
    withBusy(proposal.dedupe_key, async () => {
      await signalSourcesService.addSource({
        companyId: proposal.company_id, url, sourceType,
      });
      setNotice(`Now watching ${proposal.company} via ${url}`);
      await refetchBoth();
    });

  // The confirmation has to describe what actually happened, and for a dismiss that
  // depends on whether the company was already monitored — one refuses the company,
  // the other drops a page. Saying "Dismissed" for both would misreport the
  // destructive one as routine.
  const acceptNotice = (proposal, action) => {
    if (action === 'accept') {
      const n = (proposal.pages || []).length;
      return `Now watching ${proposal.company} — ${n} ${n === 1 ? 'page' : 'pages'} added.`;
    }
    if (proposal.monitored) return 'Page skipped — we won’t propose it again.';
    return 'Dismissed — we won’t propose this company again.';
  };

  // watchAll accepts every bundle currently on screen, pages included.
  //
  // Client-side loop over the single-row decide endpoint, like the other bulk action
  // on this page, so it is NOT atomic across companies: it reports how many actually
  // landed rather than claiming success. Each individual accept still is atomic —
  // the API writes monitoring and pages in one transaction — so a partial run leaves
  // completed companies fully watched, never monitored-but-blind. Scoped to the
  // loaded page deliberately: firing the whole backlog would be hundreds of round
  // trips from the browser.
  const watchAll = async (bundles) => {
    setWatchAllBusy(true);
    try {
      const results = await Promise.allSettled(bundles.map((p) =>
        signalSourcesService.decide({
          kind: p.kind, dedupeKey: p.dedupe_key, action: 'accept',
          payload: { company_id: p.company_id }, pages: p.pages || [],
        })));
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const total = bundles.length;
      if (ok === total) {
        setNotice(`Now watching ${ok} ${ok === 1 ? 'company' : 'companies'}.`);
      } else {
        // The count alone hid the cause: on staging 24 of 25 failed with the same
        // reason from the API, and the banner said nothing a user could act on.
        // Distinct reasons only, so 24 identical refusals read as one sentence.
        const reasons = [...new Set(results
          .filter((r) => r.status === 'rejected')
          .map((r) => r.reason?.message)
          .filter(Boolean))];
        const why = reasons.length ? `: ${reasons.join('; ')}` : '';
        setActionError(`Started watching ${ok} of ${total} — ${total - ok} failed${why}.`);
      }
      await refetchBoth();
    } finally {
      setWatchAllBusy(false);
    }
  };

  // showEvidence opens the records behind one reason sentence, so a claim can be
  // checked rather than taken on trust. The dialog opens immediately in a loading
  // state -- waiting for the fetch before showing anything would read as a dead click.
  const showEvidence = async (proposal, reason) => {
    setEvidence({
      title: reason.text, code: reason.code, loading: true, error: '', data: null,
    });
    try {
      const data = await signalSourcesService.getEvidence({
        companyId: proposal.company_id, code: reason.code, limit: 50,
      });
      setEvidence((e) => (e && e.code === reason.code
        ? { ...e, loading: false, data } : e));
    } catch (err) {
      setEvidence((e) => (e && e.code === reason.code
        ? { ...e, loading: false, error: err.message } : e));
    }
  };

  // Switching ON is a commitment and does not happen on the click alone: it opens a
  // confirm carrying the company's rationale, and — when we hold no page for them —
  // refuses to proceed without one. That is the same rule the proposal queue
  // enforces, and without it the switch is a second route to the dead end the queue
  // was redesigned to remove: a company monitored with nothing to watch.
  //
  // Switching OFF is reversible and removes nothing, so it does not earn a dialog.
  const toggleMonitoring = (companyRow, next) => {
    if (next) {
      setWatchConfirm({ company: null, loading: true, error: '' });
      signalSourcesService.getCompany(companyRow.company_id)
        .then((c) => setWatchConfirm((w) => (w ? { ...w, company: c, loading: false } : w)))
        .catch((e) => setWatchConfirm((w) => (w ? { ...w, loading: false, error: e.message } : w)));
      return Promise.resolve();
    }
    return applyMonitoring(companyRow, false);
  };

  const applyMonitoring = (companyRow, next) =>
    withBusy(companyRow.company_id, async () => {
      await signalSourcesService.setMonitoring(companyRow.company_id, next);
      keepOnScreen(companyRow, next);
      setNotice(next
        ? `Monitoring ${companyRow.name}`
        : `Stopped monitoring ${companyRow.name} — its sources are kept`);
      await refetchBoth();
    });

  // keepOnScreen holds the row the user just acted on, so the result of their own
  // click is visible rather than the row disappearing under them.
  //
  // It NORMALISES the row, because it is called with two different shapes: a company
  // from the page payload (which carries sources and dates) and a CompanyDetail from
  // getCompany (which carries a rationale and neither). Held rows are rendered by
  // CompanyGroup, whose companyLine reads company.sources.length — so a detail
  // object went in and took the whole page down with "Cannot read properties of
  // undefined (reading 'length')".
  //
  // Defaulted here rather than guarded in companyLine on purpose: every company the
  // API returns really does carry `sources`, so a missing one is a bug in what this
  // function was handed, and swallowing it downstream would only move the next such
  // bug somewhere harder to see. The known-good fields are preferred from the page
  // row when we have one, since the detail shape has no dates to show.
  const keepOnScreen = (companyRow, signalsOn) =>
    setJustToggled((rows) => {
      const existing = rows.find((r) => r.company_id === companyRow.company_id);
      const known = (page?.companies || []).find((c) => c.company_id === companyRow.company_id);
      return [
        ...rows.filter((r) => r.company_id !== companyRow.company_id),
        {
          last_job_start: null,
          last_activity_at: null,
          ...existing,
          ...known,
          ...companyRow,
          sources: companyRow.sources || known?.sources || existing?.sources || [],
          signals_on: signalsOn,
          just_toggled: true,
        },
      ];
    });

  // showMore appends the next page. The company list is 13,570 rows on the dev
  // tenant and was pinned to offset 0, so everything past the first 50 was
  // unreachable — including, on the 'Not watched' filter, the only company holding
  // a source. Appending rather than replacing keeps what the user was already
  // looking at on screen.
  const showMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await signalSourcesService.getPage({
        show: show || 'monitoring', limit: PAGE_SIZE, offset: fetched.length,
      });
      setMore((rows) => [...rows, ...(data.companies || [])]);
    } catch (e) {
      setActionError(e.message);
    } finally {
      setLoadingMore(false);
    }
  };

  // confirmWatch takes the company as-is; supplyPageAndWatch is the pageless path,
  // where the page and the monitoring are written together by one request.
  const confirmWatch = async (c) => {
    setWatchConfirm(null);
    await applyMonitoring(c, true);
  };
  const supplyPageAndWatch = async (c, url, sourceType) => {
    setWatchConfirm(null);
    await withBusy(c.company_id, async () => {
      await signalSourcesService.addSource({ companyId: c.company_id, url, sourceType });
      keepOnScreen(c, true);
      setNotice(`Now watching ${c.name} via ${url}`);
      await refetchBoth();
    });
  };

  const toggleSource = (source, next) =>
    withBusy(source.source_id, async () => {
      await signalSourcesService.patchSource(source.source_id, { is_active: next });
      await refetchBoth();
    });

  const changeType = (source, sourceType) =>
    withBusy(source.source_id, async () => {
      await signalSourcesService.patchSource(source.source_id, { source_type: sourceType });
      await refetchBoth();
    });

  const removeSource = (source) =>
    withBusy(source.source_id, async () => {
      await signalSourcesService.removeSource(source.source_id);
      setNotice('Removed.');
      await refetchBoth();
    });

  // Bulk actions loop the single-row endpoints, so they are NOT atomic. Report what
  // actually happened rather than claiming success (V5-4).
  const runBulk = async (verb, fn) => {
    if (bulkBusyRef.current) return;
    bulkBusyRef.current = true;
    setBulkBusy(true);
    try {
      const ids = [...selected];
      const results = await Promise.allSettled(ids.map(fn));
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      setNotice(ok === ids.length
        ? `${verb} ${ok} source${ok === 1 ? '' : 's'}.`
        : `${verb} ${ok} of ${ids.length} — ${ids.length - ok} failed.`);
      setSelected(new Set());
      await refetchBoth();
    } finally {
      bulkBusyRef.current = false;
      setBulkBusy(false);
    }
  };

  const toggleSelect = (source) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(source.source_id)) n.delete(source.source_id); else n.add(source.source_id);
      return n;
    });

  // Fetched at open time, wide enough (limit:500) to cover a tenant with more
  // The dialog no longer preloads a company list at all: it searches server-side as
  // the user types, over the whole book rather than the monitored slice.
  const openDialog = () => {
    setDialog(true);
    setDialogError('');
  };

  // Returns the raw match rows to the dialog, which owns its own debounce and
  // race-guard. Errors propagate so the dialog can SAY the search failed rather
  // than render an empty list, which would read as "you have no such company".
  const searchCompanies = async (q) => {
    const data = await signalSourcesService.searchCompanies(q);
    return data.companies || [];
  };

  const addSource = async (form) => {
    if (addBusyRef.current) return;
    addBusyRef.current = true;
    setAddBusy(true);
    try {
      setDialogError('');
      await signalSourcesService.addSource(form);
      setDialog(false);
      setNotice('Added — private to your workspace.');
      await refetchBoth();
    } catch (e) {
      setDialogError(e.message);
    } finally {
      addBusyRef.current = false;
      setAddBusy(false);
    }
  };

  const counts = page?.counts;
  // The refetched page, plus any row the user just toggled that no longer matches
  // the filter. Merged rather than concatenated so a company still in the payload
  // is not rendered twice.
  const fetched = [...(page?.companies || []), ...more];
  const fetchedIds = new Set(fetched.map((c) => c.company_id));
  const companies = [
    ...fetched,
    ...justToggled.filter((c) => !fetchedIds.has(c.company_id)),
  ];

  // How many companies this FILTER has, from the tenant-wide counts rather than the
  // rows on screen. Deriving it from the page would make the list claim it was
  // complete the moment a page came back short.
  const filterTotal = !counts ? 0
    : show === 'monitoring' ? counts.monitored
      : show === 'unwatched' ? counts.total - counts.monitored
        : counts.total;
  const hasMore = fetched.length < filterTotal;

  return (
    <Layout user={user}>
      <div className="p-8 max-w-5xl">
        <h1 className="text-2xl font-bold text-gray-900">Signal sources</h1>
        <p className="mt-1 text-gray-600 text-sm">
          {counts
            ? `Monitoring ${counts.monitored} of ${counts.total} companies · ${counts.sources_on_monitored} sources on the companies you watch`
            : 'The companies you monitor, and the pages we watch for each.'}
        </p>

        {notice && (
          <p className="mt-4 rounded-lg bg-gray-900 text-white text-sm px-4 py-2 inline-block">{notice}</p>
        )}
        {actionError && (
          <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {actionError}
          </p>
        )}

        {mode === 'view' ? (
          queueError ? (
            <p className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {queueError}
            </p>
          ) : (
            <div className="mt-6">
              <ProposalQueue
                proposals={proposals}
                total={queueCounts.watch + queueCounts.needs_page}
                busyKeys={busyKeys}
                onAccept={(p) => decide(p, 'accept')}
                onDismiss={(p) => decide(p, 'dismiss')}
                onSupplyPage={supplyPage}
                onWatchAll={watchAll}
                watchAllBusy={watchAllBusy}
                // A full page back means the backlog is probably longer than this.
                mayHaveMore={(queueCounts.watch + queueCounts.needs_page) > proposals.length}
                onShowEvidence={showEvidence}
              />
            </div>
          )
        ) : (
          <div className="mt-6">
            <EditBar
              count={selected.size}
              busy={bulkBusy}
              onTurnOff={() => runBulk('Turned off', (id) =>
                signalSourcesService.patchSource(id, { is_active: false }))}
              onRemove={() => runBulk('Removed', (id) => signalSourcesService.removeSource(id))}
            />
          </div>
        )}

        <div className="flex items-center gap-3 flex-wrap mb-4">
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 gap-0.5" role="tablist">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                role="tab"
                aria-selected={show === f.id}
                onClick={() => chooseFilter(f.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                  show === f.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <span className="flex-1" />
          <button
            type="button"
            onClick={() => setMode(mode === 'view' ? 'edit' : 'view')}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 text-gray-600 hover:border-gray-400"
          >
            {mode === 'view' ? 'Edit' : 'Done'}
          </button>
          <button
            type="button"
            onClick={openDialog}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#00C6AE] text-white hover:bg-[#00b09b]"
          >
            + Add a source
          </button>
        </div>

        {pageError && (
          <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{pageError}</p>
        )}

        {!pageError && !listLoading && counts && counts.total === 0 && <NoCompanies />}

        {!pageError && !listLoading && counts && counts.total > 0 && companies.length === 0 && (
          <NoneInThisView show={show} />
        )}

        {/* The skeleton REPLACES the list rather than sitting above it: leaving the
            previous filter's rows on screen is what made the tab look broken. */}
        {!pageError && listLoading && (
          <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
            <LoadingCompanies />
          </div>
        )}

        <div className="space-y-2">
          {!listLoading && companies.map((c) => (
            <CompanyGroup
              key={c.company_id}
              company={c}
              open={!!open[c.company_id]}
              mode={mode}
              selectedIds={selected}
              busyKeys={busyKeys}
              onToggleOpen={(co) => setOpen((o) => ({ ...o, [co.company_id]: !o[co.company_id] }))}
              onToggleMonitoring={toggleMonitoring}
              onToggleActive={toggleSource}
              onSelect={toggleSelect}
              onRemove={removeSource}
              onChangeType={changeType}
            />
          ))}
          {/* The list is a window onto 13,570 companies and was pinned to the first
              50, so everything past row 49 was unreachable. The total comes from the
              tenant-wide counts, never from the rows on screen — a count derived
              from the page would say "50 of 50" and hide the rest for good. */}
          {hasMore && (
            <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={showMore}
                disabled={loadingMore}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-[#00C6AE] text-[#00695C] hover:bg-teal-50 disabled:opacity-50"
              >
                {loadingMore ? 'Loading…' : 'Show more'}
              </button>
              <span className="text-xs text-gray-400 tabular-nums">
                {fetched.length} of {filterTotal}
              </span>
            </div>
          )}
        </div>

        {evidence && (
          <EvidenceDialog
            title={evidence.title}
            code={evidence.code}
            evidence={evidence.data}
            loading={evidence.loading}
            error={evidence.error}
            onClose={() => setEvidence(null)}
          />
        )}

        {watchConfirm && (
          <StartWatchingDialog
            company={watchConfirm.company}
            loading={watchConfirm.loading}
            error={watchConfirm.error}
            onConfirm={confirmWatch}
            onSupplyPage={supplyPageAndWatch}
            onCancel={() => setWatchConfirm(null)}
          />
        )}

        {dialog && (
          <AddSourceDialog
            onSearch={searchCompanies}
            error={dialogError}
            busy={addBusy}
            onAdd={addSource}
            prefill={dialogPrefill}
            onClose={() => { setDialog(false); setDialogError(''); setDialogPrefill(null); }}
          />
        )}
      </div>
    </Layout>
  );
}
